import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import CollaboratorsDataTable from '@/pages/_partials/CollaboratorsDataTable';
import SheetSendToSubsidiaries from './sheets/SheetSendToSubsidiaries';
import SheetLowBalanceAlert from './sheets/SheetLowBalanceAlert';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useAuth } from '@/lib/contexts/auth-context';
import { useBalance } from '@/lib/contexts/balance-context';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
    Building2,
    Trash2,
    ClipboardList,
    Filter,
    Camera,
    Users,
    Coins,
    Search,
    X,
    FileText,
    TrendingUp,
    UserCheck,
    Plus,
    Loader2,
    Bell,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';

export default function CompanyTabContent({
    // Company data
    company,
    companyLogoUrl,
    companyBalance,
    collaborators,
    collaboratorSearch,
    setCollaboratorSearch,
    activities,
    isLoadingActivities = false,
    // Subsidiaries
    subsidiaries = [],
    isLoadingSubsidiaries = false,
    refreshSubsidiaries,
    // Height management
    companyInfoRef,
    collaboratorsHeight,
    // Sheet openers
    setCompanyEditOpen,
    setInviteOpen,
    setEditCollaboratorOpen,
    setDeleteCollaboratorOpen,
    setActivityFilterOpen,
    setSubsidiaryCreateOpen,
    // Company state updater
    setCompany,
    // Collaborator selection
    setSelectedCollaborator,
    setCollaboratorToDelete,
    setCollaboratorToReactivate,
    setReactivateCollaboratorOpen,
    // Collaborators pagination
    collaboratorsPagination,
    // Subsidiaries pagination
    subsidiariesPagination,
    // Activities pagination
    activitiesPagination,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { can, canOnSelf, canOnDescendants, hasEditablePermissions } = usePermissions();
    const { user: authUser, isOwnerOrAdmin } = useAuth();
    const { refreshBalance } = useBalance();
    // Détection particulier : flag company.is_fake OU rôle utilisateur 'user'
    // (cas legacy en BDD sans fake company). Note : /api/user sérialise `role` comme une string (nom du rôle).
    const isIndividual = company?.is_fake === true || authUser?.role === 'user';
    const [sendToSubsidiariesOpen, setSendToSubsidiariesOpen] = useState(false);
    const [isSendingTokens, setIsSendingTokens] = useState(false);
    const [lowBalanceAlertOpen, setLowBalanceAlertOpen] = useState(false);
    const [isSavingLowBalanceAlert, setIsSavingLowBalanceAlert] = useState(false);
    const companyId = authUser?.company_uniq_id ?? authUser?.company?.uniq_id ?? null;

    const handleSendToSubsidiaries = async ({ transfers, totalToSend }) => {
        setIsSendingTokens(true);
        try {
            for (const transfer of transfers) {
                await horodatyApi.subsidiaries.transferCredits(companyId, transfer.id, transfer.amount);
            }
            toast.success(t('account:page.tokensSentToSubsidiaries', { count: totalToSend }));
            await Promise.all([refreshBalance(), refreshSubsidiaries?.(true)]);
            setSendToSubsidiariesOpen(false);
        } catch (error) {
            const message = error?.response?.data?.message || t('account:erreurLorsDeLEnvoiDesJetons');
            toast.error(message);
        } finally {
            setIsSendingTokens(false);
        }
    };

    const handleSubsidiaryClick = (subsidiary) => {
        navigate(`/settings/subsidiary/${subsidiary.uniq_id ?? subsidiary.id}`);
    };

    const handleLowBalanceAlert = async (data) => {
        setIsSavingLowBalanceAlert(true);
        try {
            const response = await horodatyApi.updateAlertLowBalanceToken(companyId, data);
            setCompany(response.data.data);
            toast.success(t('account:alerteEnregistreeAvecSucces'));
            setLowBalanceAlertOpen(false);
        } catch (error) {
            const message = error?.response?.data?.message || t('account:erreurLorsDeLEnregistrementDeLAlerte');
            toast.error(message);
        } finally {
            setIsSavingLowBalanceAlert(false);
        }
    };

    // Helper pour afficher l'icône de type d'activité
    const ActivityTypeIcon = ({ type }) => {
        const iconClass = 'h-5 w-5';
        const bgClass = `w-10 h-10 rounded-full flex items-center justify-center ${
            type === 'procedure_filled'
                ? 'bg-badge-filled/20'
                : type === 'procedure_created'
                  ? 'bg-badge-created/20'
                  : type === 'photo'
                    ? 'bg-secondary/20'
                    : type === 'tokens'
                      ? 'bg-secondary/20'
                      : type === 'invitation'
                        ? 'bg-badge-invitation/20'
                        : 'bg-surface'
        }`;

        return (
            <div className={bgClass}>
                {type === 'procedure_filled' && <FileText className={`${iconClass} text-badge-filled`} />}
                {type === 'procedure_created' && <ClipboardList className={`${iconClass} text-badge-created`} />}
                {type === 'photo' && <Camera className={`${iconClass} text-secondary`} />}
                {type === 'tokens' && <TrendingUp className={`${iconClass} text-secondary`} />}
                {type === 'invitation' && <UserCheck className={`${iconClass} text-badge-invitation`} />}
                {!type && <ClipboardList className={`${iconClass} text-muted-foreground`} />}
            </div>
        );
    };

    // Colonnes pour la DataTable des sous-comptes
    const isSuperAdmin = authUser?.role === 'admin';

    const subsidiaryColumns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: t('account:nom2'),
                cell: ({ row }) => <span className="text-[13px] text-foreground">{row.original.name}</span>,
                meta: { hideOnMobile: true },
            },
            ...(isSuperAdmin
                ? [
                      {
                          accessorKey: 'type',
                          header: t('account:type'),
                          cell: ({ row }) => {
                              return (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
                                      {row.original.is_fake === true ? t('account:particulier') : t('account:entreprise')}
                                  </span>
                              );
                          },
                          meta: { hideOnMobile: true },
                      },
                  ]
                : []),
            {
                accessorKey: 'email',
                header: t('account:emailDeContact'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">
                        {row.original.is_fake === true
                            ? row?.original?.owner?.email || row.original.email
                            : row.original.email}
                    </span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'balance',
                header: t('account:solde'),
                cell: ({ row }) => (
                    <div className="flex items-center gap-1.5">
                        <Coins className="h-4 w-4 text-brand" />
                        <span className="text-[13px] text-foreground font-medium">
                            {row.original.is_fake === true
                                ? (row?.original?.owner?.balance ?? 0)
                                : (row.original.balance ?? 0)}
                        </span>
                    </div>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'phone',
                header: t('account:telephone'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">
                        {row.original.phone ?? row.original.phone_number ?? '—'}
                    </span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'siret',
                header: 'SIRET',
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">{row.original.siret ?? '—'}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                id: 'address',
                header: t('account:adresse'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">
                        {[row.original.street, row.original.cp, row.original.city].filter(Boolean).join(', ') || '—'}
                    </span>
                ),
                meta: { hideOnMobile: true },
            },
        ],
        [isSuperAdmin, t],
    );

    // Colonnes pour la DataTable des activités
    const activityColumns = useMemo(
        () => [
            {
                accessorKey: 'type',
                header: t('account:type'),
                cell: ({ row }) => <ActivityTypeIcon type={row.original.type} />,
                meta: { className: 'w-[60px]' },
            },
            {
                accessorKey: 'title',
                header: t('account:description'),
                cell: ({ row }) => <span className="text-[13px] text-foreground">{row.original.title}</span>,
            },
            {
                accessorKey: 'date',
                header: t('account:date'),
                cell: ({ row }) => <span className="text-[13px] text-muted-foreground">{row.original.date}</span>,
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'collaborator',
                header: t('account:responsable'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">{row.original.collaborator ?? '-'}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'delta',
                header: () => <span className="text-right block">{t('account:montant')}</span>,
                cell: ({ row }) => {
                    const delta = row.original.delta;
                    if (delta === 0 || delta === undefined) {
                        return <span className="text-[14px] text-muted-foreground block text-right">-</span>;
                    }
                    return (
                        <span
                            className={`text-[15px] font-medium block text-right ${
                                delta > 0 ? 'text-secondary' : 'text-destructive'
                            }`}
                        >
                            {delta > 0 ? `+${delta}` : delta}
                        </span>
                    );
                },
                meta: { hideOnMobile: true, className: 'text-right' },
            },
        ],
        [t],
    );

    return (
        <div className="px-8 space-y-6">
            {/* Top: Info + Collaborators */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div ref={companyInfoRef} className="space-y-6">
                    {/* Company Info */}
                    <div className="flex flex-col bg-card rounded-2xl overflow-hidden border border-border">
                        {/* Company Header with gradient */}
                        <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 w-full p-[25px] flex flex-col items-center border-b border-border relative">
                            {
                                /* can('UPDATE_COMPANY_INFO') */ isOwnerOrAdmin() && (
                                    <Button
                                        variant="solid"
                                        color="dark"
                                        size="xmd"
                                        onClick={() => setCompanyEditOpen(true)}
                                        className="absolute top-4 right-4 text-[13px]"
                                    >
                                        {t('account:modifier')}
                                    </Button>
                                )
                            }
                            <div
                                className={`h-[60px] w-[60px] flex items-center justify-center mb-3 overflow-hidden ${companyLogoUrl ? '' : 'bg-secondary rounded-full'}`}
                            >
                                {companyLogoUrl ? (
                                    <img src={companyLogoUrl} alt={t('account:logo')} className="w-full h-full object-contain" />
                                ) : (
                                    <Building2 className="h-7 w-7 text-secondary-foreground" />
                                )}
                            </div>
                            <h2 className="text-[18px] text-foreground mb-[5px] text-center">{company?.name ?? '-'}</h2>
                            <p className="text-[14px] text-muted-foreground">{t('account:siret')} {company?.siret ?? '-'}</p>
                        </div>

                        {/* Company Details */}
                        <div className="p-[21px] space-y-4 flex-1">
                            <div>
                                <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                    {t('account:nTvaIntracommunautaire')}
                                </label>
                                <p className="text-[13px] text-foreground">{company?.vat ?? '—'}</p>
                            </div>
                            <div>
                                <label className="text-[13px] text-muted-foreground mb-[6px] block">{t('account:description')}</label>
                                <p className="text-[13px] text-foreground">{company?.description ?? '—'}</p>
                            </div>
                            <div>
                                <label className="text-[13px] text-muted-foreground mb-[6px] block">{t('account:notes')}</label>
                                <p className="text-[13px] text-foreground">{company?.notes ?? '—'}</p>
                            </div>
                            <div>
                                <label className="text-[13px] text-muted-foreground mb-[6px] block">{t('account:adresse')}</label>
                                <p className="text-[13px] text-foreground">
                                    {[company?.street, company?.cp, company?.city, company?.country]
                                        .filter(Boolean)
                                        .join(', ') || '—'}
                                </p>
                            </div>
                            <div>
                                <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                    {t('account:emailDeContact')}
                                </label>
                                <p className="text-[13px] text-foreground">{company?.email ?? '—'}</p>
                            </div>
                            <div>
                                <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                    {t('account:emailDeFacturation')}
                                </label>
                                <p className="text-[13px] text-foreground">{company?.billing_email ?? '—'}</p>
                            </div>
                            <div>
                                <label className="text-[13px] text-muted-foreground mb-[6px] block">{t('account:telephone')}</label>
                                <p className="text-[13px] text-foreground">{company?.phone ?? '—'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Company Token Balance */}
                    {canOnSelf('VIEW_COMPANY_TOKENS') && (
                        <div className="bg-dark rounded-2xl p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-5">
                            {/* Mobile: balance on top */}
                            <span className="md:hidden text-[48px] text-secondary font-semibold leading-none text-center">
                                {companyBalance ?? 0}
                            </span>

                            <div className="flex flex-col gap-3 md:gap-4 flex-1 items-center md:items-start">
                                <h3 className="text-[14px] md:text-[15px] text-dark-foreground font-medium text-center md:text-left">
                                    {t('account:soldeDeJetonsDeLEntreprise')}
                                </h3>
                                <div className="flex flex-col md:flex-row md:flex-wrap items-center md:items-start gap-2 w-full md:w-auto">
                                    {canOnSelf('VIEW_TOKENS') && (
                                        <Button
                                            variant="solid"
                                            color="secondary"
                                            size="auto"
                                            asChild
                                            className="rounded-lg py-2.5 px-5 text-[13px] font-medium w-full md:w-auto"
                                        >
                                            <Link to="/tokens" className="inline-block whitespace-nowrap text-center">
                                                {t('account:commander')}
                                            </Link>
                                        </Button>
                                    )}
                                    {canOnDescendants('SEND_TOKENS') && subsidiaries.length > 0 && (
                                        <Button
                                            variant="solid"
                                            color="neutral"
                                            size="auto"
                                            onClick={() => setSendToSubsidiariesOpen(true)}
                                            className="rounded-lg py-2.5 px-5 text-[13px] text-foreground font-medium bg-card hover:bg-card/90 w-full md:w-auto"
                                        >
                                            {t('account:envoyerAUnSousCompte')}
                                        </Button>
                                    )}
                                    <Button
                                        variant="solid"
                                        color="neutral"
                                        size="auto"
                                        onClick={() => setLowBalanceAlertOpen(true)}
                                        className="rounded-lg py-2.5 px-5 text-[13px] font-medium w-full md:w-auto border border-muted-foreground/40 bg-gray-900"
                                    >
                                        <Bell className="h-4 w-4" />
                                        {t('account:mAlerterQuandLeSoldeEstBas')}
                                    </Button>
                                </div>
                            </div>

                            {/* Desktop: balance on right */}
                            <span className="hidden md:block text-[56px] text-secondary font-semibold leading-none">
                                {companyBalance ?? 0}
                            </span>
                        </div>
                    )}
                </div>
                {/* Collaborators — masqué pour les particuliers (entreprise fictive ou rôle 'user') */}
                {!isIndividual && canOnSelf('LIST_USERS') && (
                    <CollaboratorsDataTable
                        collaborators={collaborators}
                        height={collaboratorsHeight}
                        onInvite={canOnSelf('CREATE_COMPANY_MEMBERS') ? () => setInviteOpen(true) : undefined}
                        onEdit={
                            canOnSelf('UPDATE_COMPANY_MEMBERS')
                                ? (c) => {
                                      setSelectedCollaborator(c);
                                      setEditCollaboratorOpen(true);
                                  }
                                : undefined
                        }
                        canEditPermissions={hasEditablePermissions('SELF')}
                        onDelete={
                            canOnSelf('ARCHIVE_COMPANY_MEMBERS')
                                ? (c) => {
                                      setCollaboratorToDelete(c);
                                      setDeleteCollaboratorOpen(true);
                                  }
                                : undefined
                        }
                        onReactivate={
                            canOnSelf('ARCHIVE_COMPANY_MEMBERS')
                                ? (c) => {
                                      setCollaboratorToReactivate(c);
                                      setReactivateCollaboratorOpen(true);
                                  }
                                : undefined
                        }
                        {...collaboratorsPagination}
                    />
                )}
            </div>

            {/* Subsidiary companies list — masqué pour les particuliers (entreprise fictive ou rôle 'user') */}

            {!isIndividual && (
                <div className="bg-card rounded-2xl overflow-hidden border border-border">
                    <div className="bg-card w-full p-5 flex items-center justify-between border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="bg-surface rounded-full h-10 w-10 flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-foreground" />
                            </div>
                            <h2 className="text-[14px] text-foreground">
                                {t('account:sousComptes')}
                                {subsidiariesPagination?.totalCount > 0 && (
                                    <span className="text-muted-foreground ml-1">
                                        ({subsidiariesPagination.totalCount})
                                    </span>
                                )}
                            </h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="relative hidden md:block">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder={t('account:rechercher')}
                                    value={subsidiariesPagination?.searchValue ?? ''}
                                    onChange={(e) => subsidiariesPagination?.onSearchChange?.(e.target.value)}
                                    className="h-9 w-[200px] rounded-lg border border-border bg-surface pl-9 pr-8 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                                />
                                {subsidiariesPagination?.searchValue && (
                                    <button
                                        onClick={() => subsidiariesPagination?.onSearchChange?.('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            {canOnSelf('CREATE_SUBSIDIARIES') && (
                                <Button
                                    variant="solid"
                                    color="primary"
                                    size="xmd"
                                    onClick={() => setSubsidiaryCreateOpen(true)}
                                >
                                    <Plus className="h-5 w-5 text-primary-foreground" />
                                    <span className="desktop-only hidden md:inline">{t('account:ajouter')}</span>
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Mobile search bar */}
                    <div className="md:hidden p-3 pb-0">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder={t('account:rechercher')}
                                value={subsidiariesPagination?.searchValue ?? ''}
                                onChange={(e) => subsidiariesPagination?.onSearchChange?.(e.target.value)}
                                className="h-9 w-full rounded-lg border border-border bg-surface pl-9 pr-8 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            {subsidiariesPagination?.searchValue && (
                                <button
                                    onClick={() => subsidiariesPagination?.onSearchChange?.('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Desktop table view */}
                    <div className="hidden md:block">
                        <DataTable
                            columns={subsidiaryColumns}
                            data={subsidiaries}
                            onRowClick={handleSubsidiaryClick}
                            emptyMessage={t('account:aucunSousCompte')}
                            {...subsidiariesPagination}
                        />
                    </div>
                    {/* Mobile card view */}
                    <div className="md:hidden p-3">
                        {subsidiariesPagination?.isLoading ? (
                            <div className="flex items-center justify-center py-3">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : subsidiaries.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-3 gap-3">
                                <p className="text-[14px] text-muted-foreground">{t('account:aucunSousCompte')}</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {subsidiaries.map((sub) => (
                                    <Button
                                        key={sub.uniq_id ?? sub.id}
                                        variant="plain"
                                        onClick={() => handleSubsidiaryClick(sub)}
                                        className="w-full h-auto p-4 bg-muted rounded-xl hover:bg-border transition-colors text-left flex-col items-start"
                                    >
                                        <div className="flex items-start justify-between mb-2 w-full">
                                            <h3 className="text-[15px] text-foreground font-medium">{sub.name}</h3>
                                            <div className="flex items-center gap-1.5 bg-brand/10 px-2 py-1 rounded-md">
                                                <Coins className="h-3 w-3 text-brand" />
                                                <span className="text-[13px] text-foreground font-medium">
                                                    {sub.balance ?? 0}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-[13px] text-muted-foreground mb-1">{sub.email}</p>
                                        <p className="text-[13px] text-muted-foreground">{sub.city ?? '—'}</p>
                                    </Button>
                                ))}
                            </div>
                        )}
                        {/* Mobile pagination */}
                        {subsidiariesPagination?.pageCount > 1 && (
                            <div className="flex items-center justify-between pt-3 border-t border-border mt-3">
                                <span className="text-[12px] text-muted-foreground">
                                    {t('account:pageLabel')} {subsidiariesPagination.currentPage} / {subsidiariesPagination.pageCount}
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        color="neutral"
                                        size="auto"
                                        className="size-8"
                                        onClick={() =>
                                            subsidiariesPagination.onPageChange(subsidiariesPagination.currentPage - 1)
                                        }
                                        disabled={subsidiariesPagination.currentPage <= 1}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        color="neutral"
                                        size="auto"
                                        className="size-8"
                                        onClick={() =>
                                            subsidiariesPagination.onPageChange(subsidiariesPagination.currentPage + 1)
                                        }
                                        disabled={
                                            subsidiariesPagination.currentPage >= subsidiariesPagination.pageCount
                                        }
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Activity history */}
            <div id="actions-history" className="bg-card rounded-2xl overflow-hidden border border-border">
                {/* Header */}
                <div className="bg-card w-full p-5 flex items-center justify-between border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="bg-surface rounded-full h-10 w-10 flex items-center justify-center">
                            <ClipboardList className="h-5 w-5 text-foreground" />
                        </div>
                        <h2 className="text-[14px] text-foreground">{t('account:historiqueDActions')}</h2>
                    </div>
                    <Button
                        variant="plain"
                        color="neutral"
                        size="auto"
                        className="w-9 h-9 bg-card rounded-full flex items-center justify-center hover:bg-surface-hover transition-colors"
                        onClick={() => setActivityFilterOpen(true)}
                    >
                        <Filter className="h-5 w-5 text-foreground" />
                    </Button>
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block">
                    <DataTable
                        columns={activityColumns}
                        data={activities}
                        emptyMessage={t('account:aucuneActivite')}
                        showPagination={true}
                        manualPagination={true}
                        pageCount={activitiesPagination?.pageCount ?? 1}
                        pageSize={activitiesPagination?.pageSize ?? 10}
                        currentPage={activitiesPagination?.currentPage ?? 1}
                        onPageChange={activitiesPagination?.onPageChange}
                        onPageSizeChange={activitiesPagination?.onPageSizeChange}
                    />
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden p-3">
                    {activitiesPagination?.isLoading ? (
                        <div className="flex items-center justify-center py-3">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : activities.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-3 gap-3">
                            <p className="text-[14px] text-muted-foreground">{t('account:aucuneActivite')}</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {activities.map((item) => (
                                <div key={item.id} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                                    <ActivityTypeIcon type={item.type} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[14px] text-foreground">{item.title}</p>
                                        <p className="text-[12px] text-muted-foreground">
                                            {item.date}
                                            {item.collaborator ? ` · ${item.collaborator}` : ''}
                                        </p>
                                    </div>
                                    {item.delta !== 0 && item.delta !== undefined && (
                                        <span
                                            className={`text-[15px] flex-shrink-0 ${
                                                item.delta > 0 ? 'text-secondary' : 'text-destructive'
                                            }`}
                                        >
                                            {item.delta > 0 ? `+${item.delta}` : item.delta}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Mobile pagination */}
                    {activitiesPagination?.pageCount > 1 && (
                        <div className="flex items-center justify-between pt-3 border-t border-border mt-3">
                            <span className="text-[12px] text-muted-foreground">
                                {t('account:pageLabel')} {activitiesPagination.currentPage} / {activitiesPagination.pageCount}
                            </span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    size="auto"
                                    className="size-8"
                                    onClick={() =>
                                        activitiesPagination.onPageChange(activitiesPagination.currentPage - 1)
                                    }
                                    disabled={activitiesPagination.currentPage <= 1}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    size="auto"
                                    className="size-8"
                                    onClick={() =>
                                        activitiesPagination.onPageChange(activitiesPagination.currentPage + 1)
                                    }
                                    disabled={activitiesPagination.currentPage >= activitiesPagination.pageCount}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sheet: Send tokens to subsidiaries */}
            <SheetSendToSubsidiaries
                open={sendToSubsidiariesOpen}
                onClose={() => setSendToSubsidiariesOpen(false)}
                subsidiaries={subsidiaries}
                onSubmit={handleSendToSubsidiaries}
                isSubmitting={isSendingTokens}
            />

            {/* Sheet: Low balance alert */}
            <SheetLowBalanceAlert
                open={lowBalanceAlertOpen}
                onClose={() => setLowBalanceAlertOpen(false)}
                company={company}
                companyBalance={companyBalance}
                onSubmit={handleLowBalanceAlert}
                isSubmitting={isSavingLowBalanceAlert}
            />
        </div>
    );
}
